/**
 * The row under the tabs (core MailFilters): Mailbox, Unread, Starred, Attachment, From, Date, Label, Clear. It sets the
 * same thing a typed query would (logic/mailQuery). With one mailbox there's no Mailbox chip. The mailbox lives in the
 * route, so it stays across folders and tabs until cleared; the rest belongs to the list in view.
 */
import type { MailboxViewModel } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import * as MailQuery from "@bundle/core/logic/mailQuery";
import { Button, Chip, ChipButton, Menu, Popover, Stack, TextField, useT, useViewModel, type MenuEntry } from "@bundle/ui";
import { useState } from "react";

const DATE_PRESETS = [
  { days: 0, key: "mail.filter.date.today" },
  { days: 7, key: "mail.filter.date.week" },
  { days: 30, key: "mail.filter.date.month" },
] as const;

export function MailFilterRow({ vm, pick, clear }: { vm: MailboxViewModel; pick: (to: { mailboxId?: string; group?: string }) => void; clear: () => void }) {
  const t = useT();
  const s = useViewModel(vm);
  const [from, setFrom] = useState<string | undefined>(undefined);
  const f = s.filters;
  const groups = Mail.mailboxGroups(s.mailboxes);
  const allUnread = Mail.mailInboxUnread(s.mailboxes, undefined);
  const count = (n: number) => (n > 0 ? { hint: String(n) } : {});
  const mailboxItems: MenuEntry[] = [
    { id: "mailbox-all", label: t("mail.filter.allMailboxes"), icon: "mail", selected: f.mailbox === undefined && f.group === undefined, ...count(allUnread), onSelect: () => pick({}) },
    { id: "mailbox-mine", separator: true, heading: t("mail.filter.myMailboxes") },
    ...Mail.mailboxMenuOrder(s.mailboxes).map((m) => ({ id: `mailbox-${m.id}`, label: m.address, icon: m.kind === "shared" ? ("users" as const) : ("mail" as const), selected: f.mailbox === m.address, ...count(m.unread), onSelect: () => pick({ mailboxId: m.id }) })),
    ...(groups.length === 0 ? [] : [{ id: "mailbox-groups", separator: true as const, heading: t("mail.filter.groups") }]),
    ...groups.map((g) => ({ id: `mailbox-group-${g}`, label: g, icon: "users" as const, selected: f.group === g, onSelect: () => pick({ group: g }) })),
  ];
  const boxLabel = f.mailbox ?? f.group;
  const labels = Mail.mergedMailLabels(s.labels);
  const dates: MenuEntry[] = DATE_PRESETS.map((d) => ({ id: `date-${d.days}`, label: t(d.key), selected: f.after === vm.dayBefore(d.days), onSelect: () => vm.setDateFilter(f.after === vm.dayBefore(d.days) ? undefined : d.days) }));
  const dateOn = DATE_PRESETS.find((d) => f.after === vm.dayBefore(d.days));
  const applyFrom = () => {
    const v = (from ?? "").trim();
    setFrom(undefined);
    vm.setFilters({ from: v === "" ? undefined : v });
  };
  const any = boxLabel !== undefined || MailQuery.hasListFilters(f);
  return (
    <Stack direction="horizontal" align="center" gap="2" paddingX="4" paddingY="2" wrap testID="mail-filters">
      {s.mailboxes.length > 1 ? (
        <Menu items={mailboxItems} testID="mail-filter-mailbox-menu">
          <ChipButton label={boxLabel === undefined ? t("mail.filter.allMailboxes") : Mail.mailboxShortName(boxLabel)} icon="mail" selected={boxLabel !== undefined} testID="mail-filter-mailbox" />
        </Menu>
      ) : null}
      <Chip label={t("mail.filter.unread")} selected={f.unread === true} onPress={() => vm.setFilters({ unread: f.unread === true ? undefined : true })} testID="mail-filter-unread" />
      <Chip label={t("mail.filter.starred")} selected={f.starred === true} onPress={() => vm.setFilters({ starred: f.starred === true ? undefined : true })} testID="mail-filter-starred" />
      <Chip label={t("mail.filter.attachment")} selected={f.attachment === true} onPress={() => vm.setFilters({ attachment: f.attachment === true ? undefined : true })} testID="mail-filter-attachment" />
      <Popover
        open={from !== undefined}
        onClose={() => setFrom(undefined)}
        side="bottom"
        align="start"
        width="picker"
        anchor={<ChipButton label={f.from ?? t("mail.filter.from")} selected={f.from !== undefined} onClick={() => setFrom(f.from ?? "")} testID="mail-filter-from" />}
      >
        <Stack gap="2" padding="2">
          <TextField value={from ?? ""} onChange={setFrom} onSubmit={applyFrom} onCancel={() => setFrom(undefined)} placeholder={t("mail.filter.fromPlaceholder")} accessibilityLabel={t("mail.filter.from")} autoFocus testID="mail-filter-from-field" />
          <Button size="sm" label={t("mail.filter.apply")} onPress={applyFrom} testID="mail-filter-from-apply" />
        </Stack>
      </Popover>
      <Menu items={dates} testID="mail-filter-date-menu">
        <ChipButton label={dateOn === undefined ? t("mail.filter.date") : t(dateOn.key)} selected={dateOn !== undefined} testID="mail-filter-date" />
      </Menu>
      {labels.length > 0 ? (
        <Menu items={labels.map((l) => ({ id: `filter-label-${l.id}`, label: l.name, icon: "tag" as const, selected: f.label === l.name, onSelect: () => vm.setFilters({ label: f.label === l.name ? undefined : l.name }) }))} testID="mail-filter-label-menu">
          <ChipButton label={f.label ?? t("mail.filter.label")} selected={f.label !== undefined} testID="mail-filter-label" />
        </Menu>
      ) : null}
      {any ? <Button size="sm" variant="ghost" label={t("mail.filter.clear")} onPress={clear} testID="mail-filter-clear" /> : null}
    </Stack>
  );
}
